import {
  ACTION_KEYS,
  CLINIC_STATION_ROLES,
  HEALTHCARE_ROLES,
  MODULE_KEYS,
  PAGE_KEYS,
  FULL_ACCESS_ROLES,
} from "./keys";
import { Permissions } from "@/type/api";
import type { AppModuleDefinition } from "./types";

export const healthcareModules: AppModuleDefinition[] = [
  {
    key: MODULE_KEYS.HEALTHCARE,
    label: "Healthcare",
    labelKey: "alkokh.sidebar.healthcare",
    icon: "tabler-stethoscope",
    fallbackRoles: HEALTHCARE_ROLES,
    nav: {
      order: 30,
      titleKey: "alkokh.sidebar.healthcare",
      icon: "tabler-stethoscope",
      // Clinical work is the app's centre of gravity on a phone.
      mobileOrder: 20,
    },
    pages: [
      {
        key: PAGE_KEYS.HEALTHCARE_COORDINATOR,
        label: "Healthcare Coordinator",
        labelKey: "alkokh.sidebar.coordinator",
        doctype: "Appointment",
        paths: ["/healthcare/coordinator"],
        pathPrefixes: ["/healthcare/coordinator/"],
        fallbackRoles: HEALTHCARE_ROLES,
        nav: {
          titleKey: "alkokh.sidebar.coordinator",
          order: 5,
          icon: "tabler-layout-dashboard",
          to: { path: "/healthcare/coordinator" },
        },
      },
      {
        key: PAGE_KEYS.HEALTHCARE_COORDINATOR_PILOT,
        label: "Healthcare Coordinator Pilot",
        labelKey: "alkokh.sidebar.coordinatorPilot",
        doctype: "Appointment",
        paths: ["/healthcare/coordinator-pilot"],
        pathPrefixes: ["/healthcare/coordinator-pilot/"],
        fallbackRoles: HEALTHCARE_ROLES,
      },
      {
        // Chrome-free walk-in intake kiosk. Not shown in the sidebar (no `nav`);
        // Clinic Reception users are auto-redirected here and locked to it.
        key: PAGE_KEYS.CLINIC_STATION,
        label: "Clinic Station",
        labelKey: "alkokh.sidebar.clinicStation",
        doctype: "Vet Case Sheet",
        paths: ["/clinic-station"],
        fallbackRoles: CLINIC_STATION_ROLES,
      },
      {
        key: PAGE_KEYS.HEALTHCARE_CASE_SHEETS,
        label: "Healthcare Case Sheets",
        labelKey: "alkokh.sidebar.caseSheets",
        doctype: "Vet Case Sheet",
        paths: ["/healthcare/case-sheets"],
        pathPrefixes: ["/healthcare/case-sheets/"],
        fallbackRoles: HEALTHCARE_ROLES,
        nav: {
          titleKey: "alkokh.sidebar.caseSheets",
          order: 10,
          icon: "tabler-clipboard-text",
          to: { path: "/healthcare/case-sheets" },
        },
      },
      {
        key: PAGE_KEYS.HEALTHCARE_VISITS,
        label: "Healthcare Visits",
        labelKey: "alkokh.sidebar.visits",
        doctype: "Vet Visit",
        paths: ["/healthcare/visits", "/app/follow-ups"],
        pathPrefixes: ["/healthcare/visits/", "/app/visits/", "/app/follow-ups/"],
        fallbackRoles: HEALTHCARE_ROLES,
        nav: {
          titleKey: "alkokh.sidebar.visits",
          order: 20,
          icon: "tabler-stethoscope",
          flattenChildren: true,
          children: [
            {
              titleKey: "alkokh.sidebar.visits",
              icon: "tabler-clipboard-text",
              to: { path: "/healthcare/visits" },
              order: 10,
            },
            {
              titleKey: "alkokh.healthcare.followUps.title",
              icon: "tabler-phone-call",
              to: { path: "/app/follow-ups" },
              order: 20,
            },
          ],
        },
      },
      {
        key: PAGE_KEYS.HEALTHCARE_CASE_FOLLOW_UP,
        label: "Healthcare Case Follow-up",
        labelKey: "alkokh.sidebar.caseFollowUp",
        doctype: "Pet Care Episode",
        // The /healthcare/pets/ prefix also resolves the single-pet cases route
        // (/healthcare/pets/:id/cases) to this permission, so both lenses share it.
        paths: ["/healthcare/case-follow-up"],
        pathPrefixes: ["/healthcare/case-follow-up/", "/healthcare/pets/"],
        fallbackRoles: HEALTHCARE_ROLES,
        nav: {
          titleKey: "alkokh.sidebar.caseFollowUp",
          order: 25,
          icon: "tabler-clipboard-check",
          to: { path: "/healthcare/case-follow-up" },
        },
      },
      {
        key: PAGE_KEYS.HEALTHCARE_APPOINTMENTS,
        label: "Healthcare Appointments",
        labelKey: "alkokh.sidebar.appointments",
        doctype: "Appointment",
        paths: ["/healthcare/appointments"],
        pathPrefixes: ["/healthcare/appointments/"],
        fallbackRoles: HEALTHCARE_ROLES,
        nav: {
          titleKey: "alkokh.sidebar.appointments",
          order: 30,
          icon: "tabler-calendar-event",
          to: { path: "/healthcare/appointments" },
        },
      },
      {
        key: PAGE_KEYS.HEALTHCARE_PHARMACY,
        label: "Healthcare Pharmacy",
        labelKey: "alkokh.sidebar.pharmacy",
        doctype: "Vet Visit",
        paths: ["/healthcare/pharmacy"],
        pathPrefixes: ["/healthcare/pharmacy/"],
        fallbackRoles: HEALTHCARE_ROLES,
        nav: {
          titleKey: "alkokh.sidebar.pharmacy",
          order: 35,
          icon: "tabler-pill",
          to: { path: "/healthcare/pharmacy" },
        },
      },
      {
        key: PAGE_KEYS.HEALTHCARE_LABS,
        label: "Healthcare Labs",
        labelKey: "alkokh.sidebar.labs",
        doctype: "Lab",
        paths: ["/healthcare/labs"],
        pathPrefixes: ["/healthcare/labs/"],
        fallbackRoles: HEALTHCARE_ROLES,
        nav: {
          titleKey: "alkokh.sidebar.labs",
          order: 40,
          icon: "tabler-flask",
          to: { path: "/healthcare/labs" },
        },
      },
      {
        key: PAGE_KEYS.HEALTHCARE_RADIOLOGY,
        label: "Healthcare Radiology",
        labelKey: "alkokh.sidebar.radiology",
        doctype: "Imaging",
        paths: ["/healthcare/radiology"],
        pathPrefixes: ["/healthcare/radiology/"],
        fallbackRoles: HEALTHCARE_ROLES,
        nav: {
          titleKey: "alkokh.sidebar.radiology",
          order: 50,
          icon: "tabler-radioactive",
          to: { path: "/healthcare/radiology" },
        },
      },
      {
        /**
         * The preventive worklist — vaccination and deworming, one page with a kind filter.
         *
         * ⚠️ ROUTE IS `/healthcare/preventive-care`, from contract §5.1. The backend already
         * emits it from the pet dashboard's preventive-lapse insight. `/healthcare/vaccinations`
         * and `/healthcare/deworming` are GONE — §5.1 and §8 say so explicitly; both were dead
         * links that never existed in the SPA, and §8 lists them under "stop doing".
         *
         * Sits between Radiology (50) and Clinical Services (60), so it reads next to the two
         * worklists it is modelled on.
         */
        key: PAGE_KEYS.HEALTHCARE_PREVENTIVE_CARE,
        label: "Preventive Care",
        labelKey: "alkokh.sidebar.preventiveCare",
        doctype: "Preventive Care Record",
        paths: ["/healthcare/preventive-care"],
        pathPrefixes: ["/healthcare/preventive-care/"],
        fallbackRoles: HEALTHCARE_ROLES,
        nav: {
          titleKey: "alkokh.sidebar.preventiveCare",
          order: 55,
          icon: "tabler-vaccine",
          to: { path: "/healthcare/preventive-care" },
        },
      },
      {
        key: PAGE_KEYS.HEALTHCARE_SERVICES,
        label: "Healthcare Services",
        labelKey: "alkokh.sidebar.clinicalServices",
        doctype: "Appointment",
        paths: ["/healthcare/services"],
        pathPrefixes: ["/healthcare/services/"],
        fallbackRoles: HEALTHCARE_ROLES,
        nav: {
          titleKey: "alkokh.sidebar.clinicalServices",
          order: 60,
          icon: "tabler-heart-handshake",
          to: { path: "/healthcare/services" },
        },
      },
      {
        key: PAGE_KEYS.HEALTHCARE_SERVICE_PROVIDER,
        label: "Service Provider",
        labelKey: "alkokh.sidebar.serviceProvider",
        doctype: "PetCareService",
        paths: ["/healthcare/service-provider"],
        pathPrefixes: ["/healthcare/service-provider/"],
        fallbackRoles: HEALTHCARE_ROLES,
        nav: {
          titleKey: "alkokh.sidebar.serviceProvider",
          order: 62,
          icon: "tabler-user-check",
          to: { path: "/healthcare/service-provider" },
        },
      },
      {
        key: PAGE_KEYS.HEALTHCARE_PROCEDURES,
        label: "Healthcare Procedures",
        labelKey: "alkokh.sidebar.procedures",
        doctype: "Pet Procedure",
        paths: ["/healthcare/procedures"],
        pathPrefixes: ["/healthcare/procedures/"],
        fallbackRoles: HEALTHCARE_ROLES,
        nav: {
          titleKey: "alkokh.sidebar.procedures",
          order: 65,
          icon: "tabler-first-aid-kit",
          to: { path: "/healthcare/procedures" },
        },
      },
      {
        key: PAGE_KEYS.HEALTHCARE_BOARDING,
        label: "Healthcare Boarding",
        labelKey: "alkokh.sidebar.boarding",
        doctype: "Pet Boarding",
        paths: ["/healthcare/boarding"],
        pathPrefixes: ["/healthcare/boarding/"],
        fallbackRoles: HEALTHCARE_ROLES,
        nav: {
          titleKey: "alkokh.sidebar.boarding",
          order: 70,
          icon: "tabler-bed",
          to: { path: "/healthcare/boarding" },
        },
      },
      {
        // Reached from the profile menu (QR Search), not the sidebar — so no `nav`
        // entry. Registered here so `getPageKeyForRoute` finds a key and the route
        // guard gates it; without an entry the page would be silently ungated.
        key: PAGE_KEYS.QR_SEARCH,
        label: "QR Search",
        labelKey: "alkokh.qrSearch.menu",
        doctype: "Pet",
        paths: ["/qr-search"],
        pathPrefixes: ["/qr-search/"],
        fallbackRoles: HEALTHCARE_ROLES,
      },
      {
        key: PAGE_KEYS.HEALTHCARE_RATINGS,
        label: "Ratings & Reviews",
        labelKey: "alkokh.sidebar.ratingsReviews",
        doctype: "Rating",
        paths: ["/healthcare/ratings"],
        pathPrefixes: ["/healthcare/ratings/"],
        fallbackRoles: HEALTHCARE_ROLES,
        // nav entry intentionally omitted to hide the page from the sidebar for now.
      },
      {
        // Managers only: it ranks doctors against each other, so plain Physician is left out.
        key: PAGE_KEYS.HEALTHCARE_DOCTOR_PERFORMANCE,
        label: "Doctor Performance",
        labelKey: "alkokh.sidebar.doctorPerformance",
        doctype: "Vet Visit",
        paths: ["/healthcare/doctor-performance"],
        pathPrefixes: ["/healthcare/doctor-performance/"],
        fallbackRoles: [Permissions.HealthcareAdministrator, Permissions.HealthcareCoordinator, ...FULL_ACCESS_ROLES],
        nav: {
          titleKey: "alkokh.sidebar.doctorPerformance",
          order: 75,
          icon: "tabler-trophy",
          to: { path: "/healthcare/doctor-performance" },
        },
      },
      {
        key: PAGE_KEYS.HEALTHCARE_CLINICAL_MASTER_DATA,
        label: "Clinical Master Data",
        labelKey: "alkokh.sidebar.clinicalMasterData",
        doctype: "Healthcare Practitioner",
        paths: ["/healthcare/clinical-master-data"],
        pathPrefixes: ["/healthcare/clinical-master-data/"],
        fallbackRoles: [Permissions.HealthcareAdministrator, ...FULL_ACCESS_ROLES],
        nav: {
          titleKey: "alkokh.sidebar.clinicalMasterData",
          order: 80,
          icon: "tabler-database-cog",
          to: { path: "/healthcare/clinical-master-data" },
        },
      },
      {
        // Its own page key (avoids colliding with the Clinical Master Data
        // entry). Actual access is still gated on the Clinical Master Data
        // permission via each route's `definePage` meta `permissionKey`, so
        // this page inherits CMD access without a separate grant.
        key: PAGE_KEYS.HEALTHCARE_UNIVERSAL_PRINT,
        label: "Universal Print",
        labelKey: "alkokh.sidebar.universalPrint",
        doctype: "Print Format",
        paths: ["/healthcare/clinical-master-data/print"],
        pathPrefixes: ["/healthcare/clinical-master-data/print/"],
        fallbackRoles: [Permissions.HealthcareAdministrator, ...FULL_ACCESS_ROLES],
        nav: {
          titleKey: "alkokh.sidebar.universalPrint",
          order: 81,
          icon: "tabler-printer",
          to: { path: "/healthcare/clinical-master-data/print" },
        },
      },
      {
        key: PAGE_KEYS.HEALTHCARE_SETTINGS,
        label: "Healthcare Settings",
        labelKey: "alkokh.healthcare.settings.title",
        doctype: "Healthcare Practitioner",
        paths: ["/healthcare/settings"],
        pathPrefixes: ["/healthcare/settings/"],
        fallbackRoles: [Permissions.HealthcareAdministrator, ...FULL_ACCESS_ROLES],
      },
    ],
    actions: [
      { key: ACTION_KEYS.HEALTHCARE_APPOINTMENTS_CREATE, label: "Create appointment", labelKey: "Create appointment", pageKey: PAGE_KEYS.HEALTHCARE_APPOINTMENTS, fallbackRoles: HEALTHCARE_ROLES },
      { key: ACTION_KEYS.HEALTHCARE_APPOINTMENTS_EXPORT, label: "Export appointments", labelKey: "Export appointments", pageKey: PAGE_KEYS.HEALTHCARE_APPOINTMENTS, fallbackRoles: HEALTHCARE_ROLES },
      { key: ACTION_KEYS.HEALTHCARE_APPOINTMENTS_PRINT, label: "Print appointments", labelKey: "Print appointments", pageKey: PAGE_KEYS.HEALTHCARE_APPOINTMENTS, fallbackRoles: HEALTHCARE_ROLES },
      { key: ACTION_KEYS.HEALTHCARE_APPOINTMENTS_CONVERT, label: "Convert appointment to visit", labelKey: "Convert appointment to visit", pageKey: PAGE_KEYS.HEALTHCARE_APPOINTMENTS, fallbackRoles: [Permissions.Physician, Permissions.HealthcareAdministrator, ...FULL_ACCESS_ROLES] },
    ],
    shortcuts: [
      {
        icon: "tabler-calendar-plus",
        title: "Appointments",
        subtitle: "Appointments",
        to: { path: "/healthcare/appointments" },
        accessKey: PAGE_KEYS.HEALTHCARE_APPOINTMENTS,
        order: 10,
      },
    ],
    search: [
      {
        group: "Operations",
        icon: "tabler-calendar-plus",
        title: "Appointments",
        url: { path: "/healthcare/appointments" },
        accessKey: PAGE_KEYS.HEALTHCARE_APPOINTMENTS,
        order: 10,
      },
      {
        group: "Settings",
        icon: "tabler-database-cog",
        title: "Clinical Master Data",
        url: { path: "/healthcare/clinical-master-data" },
        accessKey: PAGE_KEYS.HEALTHCARE_CLINICAL_MASTER_DATA,
        order: 90,
      },
    ],
  },
  {
    key: MODULE_KEYS.QUEUE,
    label: "Queue",
    labelKey: "alkokh.sidebar.queue",
    icon: "tabler-list-check",
    fallbackRoles: HEALTHCARE_ROLES,
    pages: [
      {
        key: PAGE_KEYS.QUEUE_DOCTOR,
        label: "Queue Doctor",
        labelKey: "alkokh.sidebar.queue",
        doctype: "Appointment",
        paths: ["/queue/doctor"],
        fallbackRoles: HEALTHCARE_ROLES,
      },
    ],
  },
];
